/**
 * The funnel's first question: **what did I just get?**
 *
 * The calendar maker has exactly one input. Text, a screenshot of an email, a flyer photo, a
 * voice memo, a PDF itinerary, an attached document — all of it arrives through the same
 * surface, and the user never pre-declares which one it is. This module is the deterministic
 * half of that: it classifies whatever came in into the small set of media kinds the extractor
 * knows how to read, so capability grows by teaching the funnel a new *kind of input* rather
 * than by adding another button.
 *
 * Classification is by media type only. The *source context* (is this image a flyer or a
 * screenshot of an email?) is the extractor's job — see `@project/inference` — because that
 * needs to read the content. Here we only route.
 */

/** The media kinds the funnel understands. Anything else is `unsupported`. */
export type IntakeMediaKind = 'text' | 'image' | 'audio' | 'document' | 'pdf' | 'unsupported';

/** Whatever the single input was handed: typed/pasted text, or a file with a name and type. */
export interface IntakeInput {
  /** Typed or pasted text. */
  readonly text?: string;
  /** The file's name, when there is a file (`itinerary.pdf`). */
  readonly fileName?: string;
  /** The file's MIME type when the platform supplied one — trusted ahead of the name. */
  readonly mimeType?: string;
}

/** What the funnel decided it's holding. */
export interface IntakeMedia {
  readonly kind: IntakeMediaKind;
  /** One line the funnel shows while it works: "Reading the PDF…". */
  readonly label: string;
}

const LABELS: Record<IntakeMediaKind, string> = {
  text: 'Reading the text…',
  image: 'Reading the image…',
  audio: 'Listening to the recording…',
  document: 'Reading the document…',
  pdf: 'Reading the PDF…',
  unsupported: "I can't read that one",
};

const EXTENSIONS: Record<string, IntakeMediaKind> = {
  pdf: 'pdf',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  heic: 'image',
  heif: 'image',
  tiff: 'image',
  bmp: 'image',
  m4a: 'audio',
  mp3: 'audio',
  wav: 'audio',
  aac: 'audio',
  caf: 'audio',
  ogg: 'audio',
  txt: 'text',
  md: 'text',
  csv: 'text',
  eml: 'document',
  msg: 'document',
  doc: 'document',
  docx: 'document',
  rtf: 'document',
  pages: 'document',
  html: 'document',
  htm: 'document',
};

function fromMimeType(mimeType: string): IntakeMediaKind | undefined {
  const type = mimeType.trim().toLowerCase().split(';')[0] ?? '';
  if (type === '') return undefined;
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('audio/')) return 'audio';
  // An email is a document, not text: it carries headers and quoted replies the extractor reads.
  if (type.startsWith('message/') || type === 'text/html') return 'document';
  if (type.startsWith('text/')) return 'text';
  if (type.startsWith('application/')) return 'document';
  return undefined;
}

function fromFileName(fileName: string): IntakeMediaKind | undefined {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return undefined;
  return EXTENSIONS[
    fileName
      .slice(dot + 1)
      .trim()
      .toLowerCase()
  ];
}

/**
 * Route one thing dropped into the funnel to the media kind that can read it.
 *
 * MIME type wins when the platform supplied one, then the file extension, and finally plain
 * text — so pasting into the same box that accepts a photo just works. A file we can't place
 * comes back `unsupported` rather than being guessed at, because the wrong reader produces a
 * confident wrong event, which is the one failure the pre-filled form can't help the user catch.
 */
export function classifyIntakeMedia(input: IntakeInput): IntakeMedia {
  const declared = input.mimeType === undefined ? undefined : fromMimeType(input.mimeType);
  const named = input.fileName === undefined ? undefined : fromFileName(input.fileName);
  // A file picker that knows nothing says `application/octet-stream`, which lands on `document`
  // — and a PDF handed over that way would then be refused as unreadable. Where the MIME type is
  // that vague, the extension is the better witness.
  const kind =
    (isVagueMediaType(input.mimeType) ? (named ?? declared) : (declared ?? named)) ??
    (input.fileName === undefined && (input.text ?? '').trim() !== '' ? 'text' : 'unsupported');
  return { kind, label: LABELS[kind] };
}

/**
 * Types that mean "some bytes" rather than a format. A picker that knows nothing says this, and
 * anything downstream that reasons about the media type has to discount it the same way.
 */
export function isVagueMediaType(mimeType: string | undefined): boolean {
  const type = mimeType?.trim().toLowerCase().split(';')[0] ?? '';
  return type === 'application/octet-stream' || type === 'binary/octet-stream';
}
